import React from 'react';
import { render, cleanup, act, fireEvent } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { enableMocking, disableMocking, MOCK_DB } from '../mockApi';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderWithMock(element, assertions, waitMs = 500) {
  enableMocking();
  let snapshot = null;
  const logCapture = createLogCapture();
  try {
    let result;
    // Phase 1: render and flush initial effects so useEffect API calls are made
    await act(async () => {
      result = render(element);
    });
    // Phase 2: wait for mock responses; setState calls during this window land in
    // the act queue and are committed when the callback returns
    await act(async () => {
      await new Promise(r => setTimeout(r, waitMs));
    });
    // Phase 3: handle nested async loads — sub-components that mount during Phase 2's
    // flush (e.g. the default tab content) may schedule their own API calls; those
    // resolve ~30ms later and need a second flush window to commit their state
    await act(async () => {
      await new Promise(r => setTimeout(r, 100));
    });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    disableMocking();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

async function renderAndNavigateToTab(tabId, container, extraWait = 250) {
  // Phase 1: click fires the event; React's handler and resulting setState land in act queue
  await act(async () => {
    const btn = container.querySelector(`[data-testid="flow-subtab-${tabId}"]`);
    assert(btn !== null, `flow-subtab-${tabId} must exist for navigation`);
    fireEvent.click(btn);
  });
  // Phase 2: wait for any async data the new tab needs; API responses during this
  // window land in the act queue and are flushed when the callback returns
  await act(async () => {
    await new Promise(r => setTimeout(r, extraWait));
  });
}

async function navigateToStep(stepIdx, container, wait = 300) {
  await act(async () => {
    const btn = container.querySelector(`[data-testid="flow-step-${stepIdx}"]`);
    assert(btn !== null, `flow-step-${stepIdx} must exist`);
    fireEvent.click(btn);
  });
  await act(async () => {
    await new Promise(r => setTimeout(r, wait));
  });
}

const PROJECT_UUID = MOCK_DB.projects[0].uuid; // 'p-test-1'
const FLOW_ROUTE = `/project/${PROJECT_UUID}/flow`;

export const projectFlowTests = [

  // ─── Flow structure ─────────────────────────────────────────────────────────

  {
    id: 'flow-render',
    name: 'ProjectFlow: page container renders',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="project-flow-container"]') !== null, 'project-flow-container must be present');
        }
      );
    },
  },

  {
    id: 'flow-card-steps',
    name: 'ProjectFlowCard: all 4 step buttons rendered (Prepare, Question, Analyze, Report)',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="project-flow-card"]') !== null, 'project-flow-card must be present');
          [0, 1, 2, 3].forEach(i => {
            assert(container.querySelector(`[data-testid="flow-step-${i}"]`) !== null, `Step ${i} button must exist`);
          });
        }
      );
    },
  },

  {
    id: 'flow-breadcrumb',
    name: 'ProjectFlow: back-to-projects and back-to-overview breadcrumbs exist',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="back-to-projects-button"]') !== null, 'Back to projects breadcrumb must exist');
          assert(container.querySelector('[data-testid="back-to-overview-button"]') !== null, 'Back to overview breadcrumb must exist');
        }
      );
    },
  },

  // ─── Step 0: Prepare — tab visibility ───────────────────────────────────────

  {
    id: 'flow-prepare-subtabs-visible',
    name: 'Step 0 (Prepare): subtabs 11, 12, 15, 13, 14, 9 all present',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          [11, 12, 15, 13, 14, 9].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) !== null, `Prepare subtab ${id} must exist`);
          });
        }
      );
    },
  },

  {
    id: 'flow-prepare-other-tabs-hidden',
    name: 'Step 0 (Prepare): analyze and report subtabs are NOT rendered',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          [0, 1, 2, 4, 5, 6, 7, 8, 10].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) === null, `Subtab ${id} must NOT exist in Prepare step`);
          });
        }
      );
    },
  },

  // ─── Step 0: Research Question tab (11) ─────────────────────────────────────

  {
    id: 'flow-tab-11-renders',
    name: 'Tab 11 (Research Question): renders container with Create and AI buttons',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          const tab = container.querySelector('[data-testid="project-flow-questao-partida-tab"]');
          assert(tab !== null, 'Research Question tab must render');
          assert(tab.querySelector('[data-testid="questao-partida-container"]') !== null, 'questao-partida-container must be inside tab');
          assert(tab.querySelector('[data-testid="questao-partida-create-btn"]') !== null, 'Create manual button must exist');
          assert(tab.querySelector('[data-testid="questao-partida-ai-btn"]') !== null, 'Generate AI button must exist');
        },
        600
      );
    },
  },

  {
    id: 'flow-tab-11-items',
    name: 'Tab 11 (Research Question): mock questions are displayed as cards',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          MOCK_DB.researchQuestions.forEach(q => {
            const card = container.querySelector(`[data-testid="questao-partida-item-${q.uuid}"]`);
            assert(card !== null, `Research question card for ${q.uuid} must exist`);
            assert(card.textContent.includes(q.question.slice(0, 20)), `Card must show question text`);
          });
        },
        600
      );
    },
  },

  // ─── Step 0: Research Goals tab (12) ────────────────────────────────────────

  {
    id: 'flow-tab-12-renders',
    name: 'Tab 12 (Research Goals): renders container with Create and AI buttons',
    run: async () => {
      const ResearchGoalsTab = require('../../Components/Research/ResearchGoalsTab').default;
      return await renderWithMock(
        withWrapper(<ResearchGoalsTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const c = container.querySelector('[data-testid="research-goals-container"]');
          assert(c !== null, 'research-goals-container must render');
          assert(c.querySelector('[data-testid="research-goals-create-btn"]') !== null, 'Create manual button must exist');
          assert(c.querySelector('[data-testid="research-goals-ai-btn"]') !== null, 'Generate AI button must exist');
        },
        400
      );
    },
  },

  {
    id: 'flow-tab-12-items',
    name: 'Tab 12 (Research Goals): mock goals are displayed as cards',
    run: async () => {
      const ResearchGoalsTab = require('../../Components/Research/ResearchGoalsTab').default;
      return await renderWithMock(
        withWrapper(<ResearchGoalsTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          MOCK_DB.researchGoals.forEach(g => {
            const card = container.querySelector(`[data-testid="research-goals-item-${g.uuid}"]`);
            assert(card !== null, `Research goal card for ${g.uuid} must exist`);
            assert(card.textContent.includes(g.general_goal.slice(0, 20)), `Card must show general goal text`);
          });
        },
        400
      );
    },
  },

  // ─── Step 0: Research Hypotheses tab (15) ───────────────────────────────────

  {
    id: 'flow-tab-15-renders',
    name: 'Tab 15 (Research Hypotheses): renders container with Create and AI buttons',
    run: async () => {
      const ResearchHypothesesTab = require('../../Components/Research/ResearchHypothesesTab').default;
      return await renderWithMock(
        withWrapper(<ResearchHypothesesTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const c = container.querySelector('[data-testid="research-hypotheses-container"]');
          assert(c !== null, 'research-hypotheses-container must render');
          assert(c.querySelector('[data-testid="research-hypotheses-create-btn"]') !== null, 'Create manual button must exist');
          assert(c.querySelector('[data-testid="research-hypotheses-ai-btn"]') !== null, 'Generate AI button must exist');
        },
        400
      );
    },
  },

  {
    id: 'flow-tab-15-items',
    name: 'Tab 15 (Research Hypotheses): mock hypotheses displayed as cards',
    run: async () => {
      const ResearchHypothesesTab = require('../../Components/Research/ResearchHypothesesTab').default;
      return await renderWithMock(
        withWrapper(<ResearchHypothesesTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          MOCK_DB.researchHypotheses.forEach(h => {
            const card = container.querySelector(`[data-testid="research-hypotheses-item-${h.uuid}"]`);
            assert(card !== null, `Hypothesis card for ${h.uuid} must exist`);
            assert(card.textContent.includes(h.hypothesis_text.slice(0, 20)), `Card must show hypothesis text`);
          });
        },
        400
      );
    },
  },

  // ─── Step 0: Methodology tab (13) ───────────────────────────────────────────

  {
    id: 'flow-tab-13-renders',
    name: 'Tab 13 (Methodology): renders container with Create and AI buttons',
    run: async () => {
      const MetodologiaTab = require('../../Components/Research/MetodologiaTab').default;
      return await renderWithMock(
        withWrapper(<MetodologiaTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const c = container.querySelector('[data-testid="metodologia-container"]');
          assert(c !== null, 'metodologia-container must render');
          assert(c.querySelector('[data-testid="metodologia-create-btn"]') !== null, 'Create manual button must exist');
          assert(c.querySelector('[data-testid="metodologia-ai-btn"]') !== null, 'Generate AI button must exist');
        },
        400
      );
    },
  },

  {
    id: 'flow-tab-13-items',
    name: 'Tab 13 (Methodology): mock methodologies are displayed as cards',
    run: async () => {
      const MetodologiaTab = require('../../Components/Research/MetodologiaTab').default;
      return await renderWithMock(
        withWrapper(<MetodologiaTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          MOCK_DB.methodologies.forEach(m => {
            const card = container.querySelector(`[data-testid="metodologia-item-${m.uuid}"]`);
            assert(card !== null, `Methodology card for ${m.uuid} must exist`);
            assert(card.textContent.includes(m.methodology_type), `Card must show methodology type`);
          });
        },
        400
      );
    },
  },

  // ─── Step 0: Characterization tab (14) ──────────────────────────────────────

  {
    id: 'flow-tab-14-renders',
    name: 'Tab 14 (Characterization): renders container with Create and AI buttons',
    run: async () => {
      const CaracterizacaoTab = require('../../Components/Research/CaracterizacaoTab').default;
      return await renderWithMock(
        withWrapper(<CaracterizacaoTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const c = container.querySelector('[data-testid="caracterizacao-container"]');
          assert(c !== null, 'caracterizacao-container must render');
          assert(c.querySelector('[data-testid="caracterizacao-create-btn"]') !== null, 'Create manual button must exist');
          assert(c.querySelector('[data-testid="caracterizacao-ai-btn"]') !== null, 'Generate AI button must exist');
        },
        400
      );
    },
  },

  {
    id: 'flow-tab-14-items',
    name: 'Tab 14 (Characterization): mock characterization cards are displayed',
    run: async () => {
      const CaracterizacaoTab = require('../../Components/Research/CaracterizacaoTab').default;
      return await renderWithMock(
        withWrapper(<CaracterizacaoTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          MOCK_DB.characterizations.forEach(c => {
            const id = c.uuid || c.id;
            const card = container.querySelector(`[data-testid="caracterizacao-item-${id}"]`);
            assert(card !== null, `Characterization card for ${id} must exist`);
            assert(card.textContent.includes(c.universe.slice(0, 15)), `Card must show universe text`);
          });
        },
        400
      );
    },
  },

  // ─── Step 0: Bibliography tab (9) ───────────────────────────────────────────

  {
    id: 'flow-tab-9-renders',
    name: 'Tab 9 (Bibliography): renders bibliography tab wrapper',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await renderAndNavigateToTab(9, container);
          const tab = container.querySelector('[data-testid="project-flow-bibliography-tab"]');
          assert(tab !== null, 'Bibliography tab wrapper must render');
        },
        600
      );
    },
  },

  // ─── Step 1: Question — tab visibility ──────────────────────────────────────

  {
    id: 'flow-question-subtabs-visible',
    name: 'Step 1 (Question): subtabs 6, 10, 16 visible; prepare/report tabs not rendered',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          [6, 10, 16].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) !== null, `Question subtab ${id} must exist`);
          });
          [11, 12, 15, 13, 14, 7, 8].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) === null, `Subtab ${id} must NOT exist in Question step`);
          });
        }
      );
    },
  },

  // ─── Step 1: Subjects tab (6) ───────────────────────────────────────────────

  {
    id: 'flow-tab-6-renders',
    name: 'Tab 6 (Subjects): renders with Create Subject, Subject Properties and Public Form buttons',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          const tab = container.querySelector('[data-testid="project-flow-subjects-tab"]');
          assert(tab !== null, 'Subjects tab must render');
          assert(tab.querySelector('[data-testid="project-flow-create-subject-btn"]') !== null, 'Create Subject button must exist');
          assert(tab.querySelector('[data-testid="project-flow-subject-properties-btn"]') !== null, 'Subject Properties button must exist');
          assert(tab.querySelector('[data-testid="project-flow-public-form-btn"]') !== null, 'Public Form button must exist');
        }
      );
    },
  },

  {
    id: 'flow-tab-6-empty-state',
    name: 'Tab 6 (Subjects): shows empty state when project has no subjects',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          const tab = container.querySelector('[data-testid="project-flow-subjects-tab"]');
          // Mock project has no subjects (subjects: [])
          const grid = tab.querySelector('[data-testid="project-flow-subjects-grid"]');
          assert(grid === null, 'Subjects grid must NOT render when project has no subjects');
        }
      );
    },
  },

  // ─── Step 1: Video Conferences tab (10) ─────────────────────────────────────

  {
    id: 'flow-tab-10-renders',
    name: 'Tab 10 (Video Conferences): renders container and Create Conference button',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          await renderAndNavigateToTab(10, container);
          const tab = container.querySelector('[data-testid="project-flow-video-conferences-tab"]');
          assert(tab !== null, 'Video Conferences tab must render');
          assert(tab.querySelector('[data-testid="video-conferences-container"]') !== null, 'video-conferences-container must exist');
          assert(tab.querySelector('[data-testid="video-conferences-create-btn"]') !== null, 'Create Conference button must exist');
        }
      );
    },
  },

  {
    id: 'flow-tab-10-empty-state',
    name: 'Tab 10 (Video Conferences): shows empty state when no conferences exist',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          await renderAndNavigateToTab(10, container);
          const empty = container.querySelector('[data-testid="video-conferences-empty"]');
          assert(empty !== null, 'Empty state must render when no conferences exist');
        }
      );
    },
  },

  // ─── Step 1: Questionnaires tab (16) ────────────────────────────────────────

  {
    id: 'flow-tab-16-renders',
    name: 'Tab 16 (Questionnaires): renders container and Create button',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          await renderAndNavigateToTab(16, container);
          const tab = container.querySelector('[data-testid="project-flow-questionnaire-tab"]');
          assert(tab !== null, 'Questionnaire tab must render');
          assert(tab.querySelector('[data-testid="questionnaire-tab-container"]') !== null, 'questionnaire-tab-container must exist');
          assert(tab.querySelector('[data-testid="questionnaire-tab-create-btn"]') !== null, 'Create button must exist');
        }
      );
    },
  },

  {
    id: 'flow-tab-16-items',
    name: 'Tab 16 (Questionnaires): mock questionnaires are displayed as cards',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          await renderAndNavigateToTab(16, container);
          MOCK_DB.questionnaires.forEach(q => {
            const card = container.querySelector(`[data-testid="questionnaire-tab-item-${q.uuid}"]`);
            assert(card !== null, `Questionnaire card for ${q.uuid} must exist`);
            assert(card.textContent.includes(q.title), `Card must show questionnaire title`);
          });
        }
      );
    },
  },

  // ─── Step 2: Analyze — tab visibility ───────────────────────────────────────

  {
    id: 'flow-analyze-subtabs-visible',
    name: 'Step 2 (Analyze): subtabs 0, 1, 2, 4, 5 visible; others not',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          [0, 1, 2, 4, 5].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) !== null, `Analyze subtab ${id} must exist`);
          });
          [6, 10, 11, 12, 15, 13, 14, 7, 8].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) === null, `Subtab ${id} must NOT exist in Analyze step`);
          });
        }
      );
    },
  },

  // ─── Step 2: Analysis tab (0) ───────────────────────────────────────────────

  {
    id: 'flow-tab-0-renders',
    name: 'Tab 0 (Analysis): renders with Upload File button and empty state',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          const tab = container.querySelector('[data-testid="project-flow-analysis-tab"]');
          assert(tab !== null, 'Analysis tab must render');
          assert(tab.querySelector('[data-testid="project-flow-upload-file-btn"]') !== null, 'Upload File button must exist');
          assert(tab.querySelector('[data-testid="project-flow-empty-analysis"]') !== null, 'Empty analysis state must show (mock has no files)');
          assert(tab.querySelector('[data-testid="project-flow-empty-upload-btn"]') !== null, 'Empty state upload button must exist');
        }
      );
    },
  },

  // ─── Step 2: ProjectGPT tab (1) ─────────────────────────────────────────────

  {
    id: 'flow-tab-1-renders',
    name: 'Tab 1 (ProjectGPT): renders chat form with document select, input and Ask button',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await renderAndNavigateToTab(1, container);
          const tab = container.querySelector('[data-testid="project-flow-gpt-tab"]');
          assert(tab !== null, 'ProjectGPT tab must render');
          assert(tab.querySelector('[data-testid="projectgpt-container"]') !== null, 'projectgpt-container must exist');
          assert(tab.querySelector('[data-testid="projectgpt-form"]') !== null, 'GPT form must exist');
          assert(tab.querySelector('[data-testid="projectgpt-question-input"]') !== null, 'Question input must exist');
          assert(tab.querySelector('[data-testid="projectgpt-ask-button"]') !== null, 'Ask button must exist');
        }
      );
    },
  },

  {
    id: 'flow-tab-1-chat-history',
    name: 'Tab 1 (ProjectGPT): chat history section renders (empty on fresh load)',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await renderAndNavigateToTab(1, container);
          const tab = container.querySelector('[data-testid="project-flow-gpt-tab"]');
          assert(tab.querySelector('[data-testid="projectgpt-chat-history"]') !== null, 'Chat history section must exist');
        }
      );
    },
  },

  // ─── Step 2: Codes/Codebook tab (2) ─────────────────────────────────────────

  {
    id: 'flow-tab-2-renders',
    name: 'Tab 2 (Codebook): renders with Add Code button and empty state',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await renderAndNavigateToTab(2, container);
          const tab = container.querySelector('[data-testid="project-flow-codes-tab"]');
          assert(tab !== null, 'Codes tab must render');
          assert(tab.querySelector('[data-testid="codebook-container"]') !== null, 'codebook-container must exist');
          assert(tab.querySelector('[data-testid="codebook-empty-state"]') !== null, 'Empty state must show (mock has no codes)');
          assert(tab.querySelector('[data-testid="codebook-empty-add-code-btn"]') !== null, 'Empty state Add Code button must exist');
        }
      );
    },
  },

  // ─── Step 2: AI Code Discovery tab (4) ──────────────────────────────────────

  {
    id: 'flow-tab-4-renders',
    name: 'Tab 4 (AI Code Discovery): renders container',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await renderAndNavigateToTab(4, container);
          const tab = container.querySelector('[data-testid="project-flow-ai-discovery-tab"]');
          assert(tab !== null, 'AI Discovery tab must render');
          assert(tab.querySelector('[data-testid="ai-code-view-container"]') !== null, 'ai-code-view-container must exist');
        }
      );
    },
  },

  // ─── Step 2: AI Code Search tab (5) ─────────────────────────────────────────

  {
    id: 'flow-tab-5-renders',
    name: 'Tab 5 (AI Code Search): renders transcription code tab',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await renderAndNavigateToTab(5, container);
          const tab = container.querySelector('[data-testid="project-flow-transcription-code-tab"]');
          assert(tab !== null, 'AI Code Search tab must render');
        }
      );
    },
  },

  // ─── Step 3: Report — tab visibility ────────────────────────────────────────

  {
    id: 'flow-report-subtabs-visible',
    name: 'Step 3 (Report): only subtabs 7 and 8 visible; all others hidden',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(3, container);
          [7, 8].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) !== null, `Report subtab ${id} must exist`);
          });
          [0, 1, 2, 4, 5, 6, 10, 11, 12, 15, 13, 14].forEach(id => {
            assert(container.querySelector(`[data-testid="flow-subtab-${id}"]`) === null, `Subtab ${id} must NOT exist in Report step`);
          });
        }
      );
    },
  },

  // ─── Step 3: Category Results tab (7) ───────────────────────────────────────

  {
    id: 'flow-tab-7-renders',
    name: 'Tab 7 (Category Results): renders empty state with Coder link button',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(3, container);
          const tab = container.querySelector('[data-testid="project-flow-results-tab"]');
          assert(tab !== null, 'Results tab must render');
          assert(tab.querySelector('[data-testid="project-flow-empty-results"]') !== null, 'Empty results state must show (mock has no coded excerpts)');
          assert(tab.querySelector('[data-testid="project-flow-empty-results-coder-btn"]') !== null, 'Go to Coder button must exist');
        }
      );
    },
  },

  // ─── Step 3: Articles tab (8) ───────────────────────────────────────────────

  {
    id: 'flow-tab-8-renders',
    name: 'Tab 8 (Articles): renders container with New Article button and empty state',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(3, container);
          await renderAndNavigateToTab(8, container);
          const tab = container.querySelector('[data-testid="project-flow-articles-tab"]');
          assert(tab !== null, 'Articles tab must render');
          assert(tab.querySelector('[data-testid="articles-tab-container"]') !== null, 'articles-tab-container must exist');
          assert(tab.querySelector('[data-testid="articles-new-btn"]') !== null, 'New Article button must exist');
          assert(tab.querySelector('[data-testid="articles-empty"]') !== null, 'Empty state must show (mock returns no articles)');
          assert(tab.querySelector('[data-testid="articles-empty-new-btn"]') !== null, 'Empty state CTA button must exist');
        }
      );
    },
  },

  {
    id: 'flow-tab-8-new-article-modal',
    name: 'Tab 8 (Articles): clicking New Article button opens CreateArticleModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(3, container);
          await renderAndNavigateToTab(8, container);
          const newBtn = container.querySelector('[data-testid="articles-new-btn"]');
          assert(newBtn !== null, 'articles-new-btn must be present before click');
          await act(async () => { fireEvent.click(newBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'CreateArticleModal dialog must open after clicking New Article');
          assert(document.body.querySelector('#title') !== null, 'title input must be in the dialog');
        }
      );
    },
  },

  {
    id: 'flow-tab-8-empty-cta-opens-modal',
    name: 'Tab 8 (Articles): clicking empty-state CTA opens CreateArticleModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(3, container);
          await renderAndNavigateToTab(8, container);
          const ctaBtn = container.querySelector('[data-testid="articles-empty-new-btn"]');
          assert(ctaBtn !== null, 'articles-empty-new-btn must be present before click');
          await act(async () => { fireEvent.click(ctaBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'CreateArticleModal dialog must open after clicking empty-state CTA');
          assert(document.body.querySelector('#title') !== null, 'title input must be in the dialog');
        }
      );
    },
  },

  // ─── Tab 0: Upload flow from within the project ─────────────────────────────

  {
    id: 'flow-tab-0-upload-btn-shows-type-selector',
    name: 'Tab 0 (Analysis): clicking Upload File button shows type selector with all 4 types',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          const uploadBtn = container.querySelector('[data-testid="project-flow-upload-file-btn"]');
          assert(uploadBtn !== null, 'project-flow-upload-file-btn must exist');
          await act(async () => { fireEvent.click(uploadBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          // UploadFileWithTypes is a headlessui Dialog — renders into document.body portal
          assert(document.body.querySelector('[data-testid="upload-type-audio"]') !== null, 'upload-type-audio must appear');
          assert(document.body.querySelector('[data-testid="upload-type-video"]') !== null, 'upload-type-video must appear');
          assert(document.body.querySelector('[data-testid="upload-type-pdf"]') !== null, 'upload-type-pdf must appear');
          assert(document.body.querySelector('[data-testid="upload-type-transcription"]') !== null, 'upload-type-transcription must appear');
        }
      );
    },
  },

  {
    id: 'flow-tab-0-empty-upload-btn-shows-type-selector',
    name: 'Tab 0 (Analysis): clicking empty-state Upload button also shows type selector',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          const emptyUploadBtn = container.querySelector('[data-testid="project-flow-empty-upload-btn"]');
          assert(emptyUploadBtn !== null, 'project-flow-empty-upload-btn must exist');
          await act(async () => { fireEvent.click(emptyUploadBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[data-testid="upload-type-audio"]') !== null, 'Type selector must appear after empty-state upload click');
        }
      );
    },
  },

  {
    id: 'flow-tab-0-select-audio-opens-audio-modal',
    name: 'Tab 0 (Analysis): selecting Audio type opens NewAudioFileUploadModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await act(async () => { fireEvent.click(container.querySelector('[data-testid="project-flow-upload-file-btn"]')); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          const audioBtn = document.body.querySelector('[data-testid="upload-type-audio"]');
          assert(audioBtn !== null, 'upload-type-audio must be present in type selector portal');
          await act(async () => { fireEvent.click(audioBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[data-testid="audio-upload-meeting-name"]') !== null, 'Audio upload modal step 1 (meeting name field) must open');
          assert(document.body.querySelector('[data-testid="audio-upload-language"]') !== null, 'Audio upload language field must be present');
        }
      );
    },
  },

  {
    id: 'flow-tab-0-select-video-opens-video-modal',
    name: 'Tab 0 (Analysis): selecting Video type opens UploadVideoFileModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await act(async () => { fireEvent.click(container.querySelector('[data-testid="project-flow-upload-file-btn"]')); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          const videoBtn = document.body.querySelector('[data-testid="upload-type-video"]');
          assert(videoBtn !== null, 'upload-type-video must be present in type selector portal');
          await act(async () => { fireEvent.click(videoBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('#file_name') !== null, 'Video upload modal step 1 (file_name field) must open');
          assert(document.body.querySelector('#language') !== null, 'Video upload language field must be present');
        }
      );
    },
  },

  {
    id: 'flow-tab-0-select-pdf-opens-pdf-modal',
    name: 'Tab 0 (Analysis): selecting PDF type opens TextUploadModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await act(async () => { fireEvent.click(container.querySelector('[data-testid="project-flow-upload-file-btn"]')); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          const pdfBtn = document.body.querySelector('[data-testid="upload-type-pdf"]');
          assert(pdfBtn !== null, 'upload-type-pdf must be present in type selector portal');
          await act(async () => { fireEvent.click(pdfBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[data-testid="pdf-upload-meeting-name"]') !== null, 'PDF upload modal step 1 must open');
          assert(document.body.querySelector('[data-testid="pdf-upload-language"]') !== null, 'PDF upload language field must be present');
        }
      );
    },
  },

  {
    id: 'flow-tab-0-select-transcription-opens-transcription-modal',
    name: 'Tab 0 (Analysis): selecting Transcription type opens TextTranscriptionModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(2, container);
          await act(async () => { fireEvent.click(container.querySelector('[data-testid="project-flow-upload-file-btn"]')); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          const transcriptionBtn = document.body.querySelector('[data-testid="upload-type-transcription"]');
          assert(transcriptionBtn !== null, 'upload-type-transcription must be present in type selector portal');
          await act(async () => { fireEvent.click(transcriptionBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('input[name="name"]') !== null, 'Transcription upload modal step 1 (name field) must open');
        }
      );
    },
  },

  // ─── Prepare tab create-modal interactions (Tab 11, 12, 13, 14, 15) ─────────

  {
    id: 'flow-tab-11-create-btn-opens-modal',
    name: 'Tab 11 (Research Question): clicking Create opens CreateManualQuestionModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          const createBtn = container.querySelector('[data-testid="questao-partida-create-btn"]');
          assert(createBtn !== null, 'questao-partida-create-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Create manual question dialog must open');
          assert(document.body.querySelector('#question') !== null, '#question input must be in the dialog');
        },
        600
      );
    },
  },

  {
    id: 'flow-tab-11-ai-btn-opens-modal',
    name: 'Tab 11 (Research Question): clicking AI button opens ResearchQuestionFormModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          const aiBtn = container.querySelector('[data-testid="questao-partida-ai-btn"]');
          assert(aiBtn !== null, 'questao-partida-ai-btn must exist');
          await act(async () => { fireEvent.click(aiBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'AI research question form modal must open');
        },
        600
      );
    },
  },

  {
    id: 'flow-tab-12-create-btn-opens-modal',
    name: 'Tab 12 (Research Goals): clicking Create opens GoalFormModal',
    run: async () => {
      const ResearchGoalsTab = require('../../Components/Research/ResearchGoalsTab').default;
      return await renderWithMock(
        withWrapper(<ResearchGoalsTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const createBtn = container.querySelector('[data-testid="research-goals-create-btn"]');
          assert(createBtn !== null, 'research-goals-create-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Research goals form dialog must open');
        },
        400
      );
    },
  },

  {
    id: 'flow-tab-15-create-btn-opens-modal',
    name: 'Tab 15 (Research Hypotheses): clicking Create opens HypothesisFormModal',
    run: async () => {
      const ResearchHypothesesTab = require('../../Components/Research/ResearchHypothesesTab').default;
      return await renderWithMock(
        withWrapper(<ResearchHypothesesTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const createBtn = container.querySelector('[data-testid="research-hypotheses-create-btn"]');
          assert(createBtn !== null, 'research-hypotheses-create-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Research hypotheses form dialog must open');
        },
        400
      );
    },
  },

  {
    id: 'flow-tab-13-create-btn-opens-modal',
    name: 'Tab 13 (Methodology): clicking Create opens MethodologyFormModal',
    run: async () => {
      const MetodologiaTab = require('../../Components/Research/MetodologiaTab').default;
      return await renderWithMock(
        withWrapper(<MetodologiaTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const createBtn = container.querySelector('[data-testid="metodologia-create-btn"]');
          assert(createBtn !== null, 'metodologia-create-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Methodology form dialog must open');
        },
        400
      );
    },
  },

  {
    id: 'flow-tab-14-create-btn-opens-modal',
    name: 'Tab 14 (Characterization): clicking Create opens CharacterizationFormModal',
    run: async () => {
      const CaracterizacaoTab = require('../../Components/Research/CaracterizacaoTab').default;
      return await renderWithMock(
        withWrapper(<CaracterizacaoTab projectUuid={PROJECT_UUID} />, '/'),
        async ({ container }) => {
          const createBtn = container.querySelector('[data-testid="caracterizacao-create-btn"]');
          assert(createBtn !== null, 'caracterizacao-create-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Characterization form dialog must open');
        },
        400
      );
    },
  },

  // ─── Question tab create-modal interactions (Tab 6, 10, 16) ─────────────────

  {
    id: 'flow-tab-6-create-btn-opens-modal',
    name: 'Tab 6 (Subjects): clicking Create Subject opens SubjectCreationModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          const createBtn = container.querySelector('[data-testid="project-flow-create-subject-btn"]');
          assert(createBtn !== null, 'project-flow-create-subject-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Subject creation dialog must open');
        }
      );
    },
  },

  {
    id: 'flow-tab-10-create-btn-opens-modal',
    name: 'Tab 10 (Video Conferences): clicking Create Conference opens ConferenceCreationModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          await renderAndNavigateToTab(10, container);
          const createBtn = container.querySelector('[data-testid="video-conferences-create-btn"]');
          assert(createBtn !== null, 'video-conferences-create-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Conference creation dialog must open');
          assert(document.body.querySelector('[name="schedule-type"]') !== null, 'schedule-type radio must be in dialog');
        }
      );
    },
  },

  {
    id: 'flow-tab-16-create-btn-opens-modal',
    name: 'Tab 16 (Questionnaires): clicking Create opens QuestionnaireInitialSetupModal',
    run: async () => {
      const ProjectFlow = require('../../Pages/Private/Settings/Projects/ProjectFlow').default;
      return await renderWithMock(
        withWrapper(<ProjectFlow />, FLOW_ROUTE),
        async ({ container }) => {
          await navigateToStep(1, container);
          await renderAndNavigateToTab(16, container);
          const createBtn = container.querySelector('[data-testid="questionnaire-tab-create-btn"]');
          assert(createBtn !== null, 'questionnaire-tab-create-btn must exist');
          await act(async () => { fireEvent.click(createBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[role="dialog"]') !== null, 'Questionnaire setup dialog must open');
          assert(document.body.querySelector('#title') !== null, '#title input must be present in dialog');
        }
      );
    },
  },

  // ─── ProjectOverview ────────────────────────────────────────────────────────

  {
    id: 'flow-overview-render',
    name: 'ProjectOverview: container, edit button and options grid render',
    run: async () => {
      const ProjectOverview = require('../../Pages/Private/Settings/Projects/ProjectOverview').default;
      return await renderWithMock(
        withWrapper(<ProjectOverview />, `/project/${PROJECT_UUID}`),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="project-overview-container"]') !== null, 'project-overview-container must be present');
          assert(container.querySelector('[data-testid="edit-project-button"]') !== null, 'Edit Project button must exist');
          assert(container.querySelector('[data-testid="project-options-grid"]') !== null, 'Options grid must exist');
        }
      );
    },
  },

  {
    id: 'flow-overview-flow-card',
    name: 'ProjectOverview: project flow card is in the bento grid',
    run: async () => {
      const ProjectOverview = require('../../Pages/Private/Settings/Projects/ProjectOverview').default;
      return await renderWithMock(
        withWrapper(<ProjectOverview />, `/project/${PROJECT_UUID}`),
        async ({ container }) => {
          assert(container.querySelector('[data-testid="project-option-flow"]') !== null, 'Project Flow option card must be in the bento grid');
        }
      );
    },
  },
];
